import React from 'react';
import { AlertTriangle, TrendingDown } from 'lucide-react';
import { crore } from './api';

export const PaceAlerts = ({ alerts, fyProgress, limit, testId = 'pace-alerts', expandable = false }) => {
  const [all, setAll] = React.useState(false);
  if (!alerts?.length) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid={`${testId}-none`}>
        Everyone is on pace — no business unit or salesperson is behind the expected target for today.
      </div>
    );
  }
  const shown = limit && !all ? alerts.slice(0, limit) : alerts;
  return (
    <div className="rounded-xl border border-red-200 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-red-50 border-b border-red-100">
        <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><TrendingDown className="w-4 h-4" /> Behind pace ({alerts.length})</p>
        {fyProgress != null && <p className="text-xs text-red-700">FY {fyProgress}% elapsed • expected = target due so far (quarter targets, or yearly × FY elapsed)</p>}
      </div>
      <div className="divide-y divide-slate-100">
        {shown.map(a => (
          <div key={`${a.kind}-${a.id}`} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm" data-testid={`${testId}-${a.kind}-${a.id}`}>
            <AlertTriangle className={`w-4 h-4 shrink-0 ${a.severity === 'critical' ? 'text-red-600' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-[160px]">
              <p className="font-medium text-slate-800">{a.name} <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">{a.kind === 'business_unit' ? 'BU' : 'Salesperson'}</span></p>
              {a.owner && <p className="text-xs text-slate-400">Owner: {a.owner}</p>}
            </div>
            <p className="text-xs text-slate-500">Won <b className="text-slate-800">{crore(a.achieved)}</b> vs expected <b className="text-slate-800">{crore(a.expected)}</b></p>
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
              {a.pace_pct ?? 0}% of pace • short {crore(a.shortfall)}
            </span>
          </div>
        ))}
      </div>
      {limit && alerts.length > limit && (expandable ? (
        <button onClick={() => setAll(!all)} className="w-full px-4 py-2 text-xs text-blue-600 bg-slate-50 hover:bg-slate-100" data-testid={`${testId}-toggle`}>{all ? 'Show less' : `Show all ${alerts.length}`}</button>
      ) : <p className="px-4 py-2 text-xs text-slate-500 bg-slate-50">+{alerts.length - limit} more in Targets &amp; Roadmap</p>)}
    </div>
  );
};

export const QuarterProgress = ({ row, title = 'Quarterly progress', testId = 'quarter-progress' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {!row.quarters_set && <span className="text-xs text-amber-700">Quarter targets not set</span>}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {row.quarters.map(q => (
        <div key={q.q} className="p-3 rounded-lg bg-slate-50" data-testid={`${testId}-${q.q}`}>
          <p className="text-xs font-semibold text-slate-500">{q.q}</p>
          <p className="text-lg font-bold text-slate-900 mt-1">{crore(q.achieved)}</p>
          <p className="text-xs text-slate-400">of {crore(q.target)} • {q.pct}%</p>
          <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
            <div className={`h-full ${q.pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(q.pct, 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const QuarterMatrix = ({ companies }) => {
  const bus = companies.filter(c => c.active).flatMap(c => c.business_units.filter(b => b.active));
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="quarter-matrix">
      <table className="w-full text-sm min-w-[820px]">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Business Unit</th>
            {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <th key={q} className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">{q} won / target</th>)}
            <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Pace</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {bus.map(b => (
            <tr key={b.id} data-testid={`qm-row-${b.id}`}>
              <td className="px-3 py-2 font-medium text-slate-800">{b.name}</td>
              {b.quarters.map(q => (
                <td key={q.q} className="px-3 py-2 text-right tabular-nums">
                  <span className="text-green-700">{crore(q.achieved)}</span> <span className="text-slate-400">/ {crore(q.target)}</span>
                </td>
              ))}
              <td className="px-3 py-2 text-right text-xs">{b.pace_pct == null ? '-' : <span className={b.pace_pct >= 90 ? 'text-green-700' : 'text-red-600'}>{b.pace_pct}%</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
